import React, { useEffect, useState } from 'react';
import { Product } from '../../../app/models/Product';
import { Place } from '../../../app/models/Place';
import { Unit } from '../../../app/models/Unit';
import { Currency } from '../../../app/models/Currency';
import { Service } from '../../../app/models/Service';
import useTransportationQuoteDetailService from '../../../app/services/hooks/useTransportationQuoteDetailService';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import {
    TransportationQuoteDetailDataForm,
    ActionResquest,
    defaultTransportationQuoteDetailDataForm,
    QuoteDataForm
} from '../../../app/models/Quote';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import QuoteDetailForm from './forms/QuoteDetailForm';
import useSweetAlert from '../../../hooks/useSweetAlert';
import { RouteSegment } from '../../../app/models/RouteSegment';
import { QuoteRouteSegment } from '../../../app/models/QuoteRouteSegment';
import useQuoteRouteSegmentService from '../../../app/services/hooks/useQuoteRouteSegmentService';
import LazyLoading from '../../../components/LazyLoading';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { QuoteDetailFormTypeEnum } from '../../../app/shared/enums';

interface Props {
    quoteId: number;
    sectorId: number;
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: () => void;
    quoteForm: QuoteDataForm;
    setQuoteForm: (quoteForm: QuoteDataForm) => void;
    currencyId?: number | string;
    reloadQuoteRouteSegmentTable: () => void;
}

const QuoteDetailCreate = ({
    quoteId,
    sectorId,
    onSaved,
    onCancel,
    onError,
    quoteForm,
    setQuoteForm,
    currencyId,
    reloadQuoteRouteSegmentTable
}: Props) => {
    const {
        createTransportationQuoteDetail,
        storeTransportationQuoteDetail,
        getQuoteRouteSegmentsToQuoteDetail,
        fetchingCreateTransportationQuoteDetail,
        fetchingStoreTransportationQuoteDetail
    } = useTransportationQuoteDetailService();

    const { fetchingShowRsToTransportationQuoteDetail, showRsToTransportationQuoteDetail } =
        useQuoteRouteSegmentService();
    const [errorFields, setErrorFields] = useState<Record<string, unknown> | undefined>();

    const [quoteDetailForm, setQuoteDetailForm] = useState<TransportationQuoteDetailDataForm>(() => ({
        ...defaultTransportationQuoteDetailDataForm,
        quote_id: quoteId,
        quantity: 0,
        currency_id: currencyId ?? defaultTransportationQuoteDetailDataForm.currency_id,
        route_segment_id: undefined,
        agreed_distance: 0,
        service_id: 0,
        product_id: 0,
        first_dispatch_date: '',
        description: ''
    }));

    const [products, setProducts] = useState<Product[]>([]);
    const [quantityUnits, setQuantityUnits] = useState<Unit[]>([]);
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [origins, setOrigins] = useState<Place[]>([]);
    const [destinies, setDestinies] = useState<Place[]>([]);
    const [services, setServices] = useState<Service[]>([]);
    const [routeSegments, setRouteSegments] = useState<RouteSegment[]>([]);
    const [quoteRouteSegments, setQuoteRouteSegments] = useState<QuoteRouteSegment[]>([]);
    const [action, setAction] = useState<ActionResquest>({});

    useEffect(() => {
        
        if (!sectorId) {
            useReactConfirmAlert().errorAlert({
                title: 'Error',
                message: 'Debe seleccionar un sector antes de agregar una posición.'
            });
            if (onError) onError();
            return;
        }

        createTransportationQuoteDetail(sectorId, {
            onSuccess: (response: ServiceResponse) => {
                setProducts(response.data.products);
                setQuantityUnits(response.data.quantity_units);
                setCurrencies(response.data.currencies);
                setOrigins(response.data.origins);
                setDestinies(response.data.destinies);
                setServices(response.data.services);
                setRouteSegments(response.data.route_segments);
                setAction(response.data.actions);

                setQuoteDetailForm((currentDetail) => ({
                    ...currentDetail,
                    action_to_do: response.data.actions.first_time
                }));
            },
            onError: (response: ServiceResponse) => {
                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
                if (onError) onError();
            }
        });
    }, []);

    useEffect(() => {
        if (services.length) {
            const firstService = services[0];
            const isTransportMaterial =
                firstService.quote_detail_form_type === QuoteDetailFormTypeEnum.TRANSPORT;

            setQuoteDetailForm({
                ...quoteDetailForm,
                service_id: firstService.id,
                ...(!isTransportMaterial && {
                    description: '',
                    route_segment_id: undefined,
                    distance: undefined,
                    agreed_distance: 0,
                    rate_runner: undefined,
                    currency_rate_runner_id: undefined,
                    quantity_unit_rate_runner_id: undefined,
                    rate_transporter: undefined,
                    currency_rate_transporter_id: undefined,
                    quantity_unit_rate_transporter_id: undefined
                })
            });
        }
    }, [services]);

    useEffect(() => {
        if (!quoteDetailForm.service_id || !services.length) return;

        const selected = services.find(
            (s) => String(s.id) === String(quoteDetailForm.service_id)
        );
        const isTransportMaterial =
            selected?.quote_detail_form_type === QuoteDetailFormTypeEnum.TRANSPORT;

        if (!isTransportMaterial) {
            setQuoteDetailForm((prev) => ({
                ...prev,
                description: '',
                route_segment_id: undefined,
                distance: undefined,
                agreed_distance: 0,
                rate_runner: undefined,
                currency_rate_runner_id: undefined,
                quantity_unit_rate_runner_id: undefined,
                rate_transporter: undefined,
                currency_rate_transporter_id: undefined,
                quantity_unit_rate_transporter_id: undefined
            }));
        }
    }, [quoteDetailForm.service_id]);

    useEffect(() => {
        setQuoteRouteSegments([]);

        if (quoteDetailForm.route_segment_id) {
            const selectedRouteSegmentId = quoteDetailForm.route_segment_id;

            showRsToTransportationQuoteDetail(selectedRouteSegmentId, {
                onSuccess: (response: ServiceResponse) => {
                    setErrorFields(undefined);
                    setQuoteDetailForm((currentDetail) => ({
                        ...currentDetail,
                        ...defaultTransportationQuoteDetailDataForm,
                        ...response.data.route_segment,
                        action_to_do: action.first_time
                    }));
                }
            });
        }
    }, [quoteDetailForm.route_segment_id]);

    const store = () => {
        useSweetAlert().spinnerAlert('Guardando detalle Cotización...', 400);

        storeTransportationQuoteDetail(quoteDetailForm, {
            onSuccess: (response: ServiceResponse) => {
                useSweetAlert().closeSpinnerAlert();
                if (response.data.continue) {
                    questionAction(response.message);

                    return;
                }
                reloadQuoteRouteSegmentTable();
                setQuoteForm({ ...quoteForm, ...response.data.quote_date });
                toast.success(response.message);
                if (onSaved) onSaved();
            },
            onError: (response: ServiceResponse) => {
                useSweetAlert().closeSpinnerAlert();
                toast.error(response.message);
                if (onError) onError();
            },
            onFieldError: (errorFields: ServiceResponse) => {
                useSweetAlert().closeSpinnerAlert();
                setErrorFields(errorFields.data);
            }
        });
    };

    const questionAction = (message: string) => {
        useSweetAlert().requestConfirmationTwoOptions({
            title: '¿Qué desea hacer?',
            text: message,
            addButtonText: 'Agregar opción',
            updateButtonText: 'Actualizar opción',
            onConfirmAdd: () => {
                quoteDetailForm.action_to_do = action.add;

                store();
            },
            onConfirmUpdate: () => {
                const quoteIdValue = quoteDetailForm.quote_id;
                const routeSegmentId = quoteDetailForm.route_segment_id;

                if (quoteIdValue == null || routeSegmentId == null) {
                    toast.error('No se pudo determinar la ruta seleccionada.');
                    return;
                }

                useSweetAlert().spinnerAlert('Consultando opciones de ruta...', 400);
                getQuoteRouteSegmentsToQuoteDetail(quoteIdValue, routeSegmentId, {
                    onSuccess: (response: ServiceResponse) => {
                        useSweetAlert().closeSpinnerAlert();

                        setQuoteDetailForm({ ...quoteDetailForm, action_to_do: action.update });
                        setQuoteRouteSegments(response.data.quote_route_segments);
                    },
                    onError: (response: ServiceResponse) => {
                        useSweetAlert().closeSpinnerAlert();
                        reloadQuoteRouteSegmentTable();
                        toast.error(response.message);
                    }
                });
            },
            onCancel: () => {
                useSweetAlert().infoAlert({
                    title: 'Cancelado',
                    text: 'No se ha ejecutado ninguna acción.'
                });
            }
        });
    };

    const cancel = () => {
        if (onCancel) onCancel();
    };

    return fetchingCreateTransportationQuoteDetail ? (
        <LazyLoading height={300} />
    ) : (
        <>
            <div className="row">
                <div className="col-12">
                    <QuoteDetailForm
                        products={products}
                        quantityUnits={quantityUnits}
                        currencies={currencies}
                        origins={origins}
                        destinies={destinies}
                        services={services}
                        routeSegments={routeSegments}
                        quoteDetailForm={quoteDetailForm}
                        setQuoteDetailForm={setQuoteDetailForm}
                        errorFields={errorFields}
                        quoteRouteSegments={quoteRouteSegments}
                        fetching={fetchingShowRsToTransportationQuoteDetail}
                    />
                    {/* <QuoteRouteSegmentToQuoteDetailTable
                        quoteRouteSegments={quoteRouteSegments}
                        
                        quoteDetailForm={quoteDetailForm}
                        setQuoteDetailForm={setQuoteDetailForm}
                       
                        /> */}
                </div>
            </div>
            <hr />
            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm
                        callbackCancel={cancel}
                        locked={fetchingStoreTransportationQuoteDetail}
                    />
                </div>
                <div className="col-auto">
                    <ButtonSaveForm
                        callbackSave={store}
                        locked={fetchingStoreTransportationQuoteDetail}
                    />
                </div>
            </div>
        </>
    );
};

export default QuoteDetailCreate;
